import axios from "axios";
import {
  AUTH_TOKEN,
  DOMAIN_ID,
  ORG_ID,
  REACT_APP_API,
  REACT_APP_API_TENANTS_URL,
  REACT_APP_URL,
} from "../constants";

// Helper to construct full URLs
const buildLoginUrl = (domain, path) => {
  if (!domain) {
    console.error("Domain is undefined for buildLoginUrl, returning path only:", path);
    return path;
  }
  const protocol = domain.startsWith("localhost") ? "http" : "https";
  return `${protocol}://${domain}${path}`;
};

// Helper to parse HTML string, create a form, and submit it
const parseAndSubmitSamlForm = (htmlString) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const formElement = doc.querySelector("form");

    if (!formElement) {
      console.error("SAML_FORM_SUBMIT_ERROR: No form element found in the HTML response.");
      return false; // Indicate failure
    }

    const action = formElement.getAttribute("action");
    const method = formElement.getAttribute("method") || "POST";

    if (!action) {
      console.error("SAML_FORM_SUBMIT_ERROR: Form action attribute not found in the HTML response.");
      return false; // Indicate failure
    }

    const newForm = document.createElement("form");
    newForm.setAttribute("method", method);
    newForm.setAttribute("action", action);
    newForm.style.display = "none"; // Make the form invisible

    const inputs = formElement.querySelectorAll("input[type='hidden']");
    inputs.forEach(input => {
      const newHiddenInput = document.createElement("input");
      newHiddenInput.setAttribute("type", "hidden");
      newHiddenInput.setAttribute("name", input.getAttribute("name"));
      newHiddenInput.setAttribute("value", input.getAttribute("value"));
      newForm.appendChild(newHiddenInput);
    });

    document.body.appendChild(newForm);
    console.log(`SAML_FORM_SUBMIT: Submitting dynamically created form to IdP logout URL: ${action}`);
    newForm.submit(); // This will trigger browser navigation
    return true; // Indicate submission was successfully initiated

  } catch (e) {
    console.error("SAML_FORM_SUBMIT_ERROR: Error during parsing or submitting SAML form:", e);
    return false; // Indicate failure
  }
};


// Helper to perform the backend logout API call
const performBackendLogout = async (authMethod, userName) => {
  let response;
  let xAuthorizationValue = AUTH_TOKEN(); // Assuming AUTH_TOKEN() provides the full, correct header value

  // If AUTH_TOKEN needs domain and orgId to be constructed:
  // const tokenPart = AUTH_TOKEN_PART(); // hypothetical function for just the token
  // const domain = DOMAIN_ID();
  // const org = ORG_ID();
  // if (tokenPart && domain && org) {
  //   xAuthorizationValue = `${tokenPart}&DOMAIN&${domain}&ORGID&${org}`;
  // }

  if (authMethod === "NORMAL") {
    try {
      response = await axios.get(`${REACT_APP_API}/okta/Logout`, { // Consider if this URL is correct
        headers: { "X-Authorization": xAuthorizationValue },
      });
      console.log("NORMAL Logout response data:", response.data);
      return true; // Client-side logout should proceed
    } catch (error) {
      console.error("NORMAL logout API request failed:", error.message);
      return true; // Allow client-side cleanup as fallback
    }
  } else if (authMethod === "OKTA" || authMethod === "ONELOGIN") {
    console.log(`Attempting ${authMethod} logout via API: ${REACT_APP_API}/v1/Logout`);
    try {
      response = await axios.get(`${REACT_APP_API}/v1/Logout`, {
        headers: { "X-Authorization": xAuthorizationValue },
      });
      console.log(`${authMethod} Logout API response received from your backend.`, response);

      if (response.data && typeof response.data === 'string' && response.data.toLowerCase().includes('<form')) {
        console.log("HTML form data received from your backend. Attempting dynamic IdP form submission...");
        const submissionInitiated = parseAndSubmitSamlForm(response.data);
        
        if (submissionInitiated) {
          // Form submission to IdP has been initiated and will navigate away.
          // Subsequent client-side code in CheckAuthnMethod (localStorage.clear, redirect) should not run.
          return false; 
        } else {
          // Parsing or initial submission logic failed.
          console.warn("Dynamic SAML form submission to IdP failed. Proceeding with standard client logout as fallback.");
          return true; // Allow client-side cleanup
        }
      } else {
        console.warn(`${authMethod} logout response from your backend was not the expected HTML form. Data:`, response.data);
        return true; // Allow client-side cleanup
      }
    } catch (error) { // Catches errors from the axios.get call to your /v1/Logout
      const errorMessage = error.response ? JSON.stringify(error.response.data) : error.message;
      console.error(`${authMethod} logout API request to your backend failed: ${errorMessage}. Proceeding with client-side logout as fallback.`);
      return true; // Allow client-side cleanup
    }
  } else {
    console.log(`Backend logout not applicable or configured for authMethod: ${authMethod}`);
    return true; // Client-side logout should proceed
  }
};

// Main function
export const CheckAuthnMethod = () => {
  let currentDomain = localStorage.getItem("URL");

  if (!currentDomain) {
    console.error("Critical: URL not found in localStorage. Logout may redirect incorrectly.");
    localStorage.clear();
    window.location.replace("/login");
    return;
  }

  const apiRequestData = {
    url:
      currentDomain === "localhost:5173" || currentDomain.startsWith("localhost:")
        ? "default.devsetup.authnull.com"
        : currentDomain,
  };

  axios
    .post(`${REACT_APP_API_TENANTS_URL}/getAuthMethod`, apiRequestData)
    .then(async (res) => {
      console.log("Authentication method API response (full):", res); // Keep this for debugging
      if (!res.data) {
        console.error("Authentication method API response data is missing. Proceeding with fallback logout.");
        localStorage.clear();
        window.location.replace(buildLoginUrl(currentDomain, "/tenant/login"));
        return;
      }

      const authMethod = res.data?.authenticationmethod?.toUpperCase();
      const ssoUrl = res.data?.ssoUrl; // This is likely the IdP login URL
      let shouldContinueClientSideLogout = true;

      if (authMethod === "NORMAL" || authMethod === "OKTA" || authMethod === "ONELOGIN") {
        let userNameForLogout = localStorage.getItem("UserName");
        if ((authMethod === "OKTA" || authMethod === "ONELOGIN") && !userNameForLogout) {
            console.warn(`UserName (email) not found for ${authMethod} logout. Backend SAML logout might be affected if it's required by your backend.`);
        }
        
        try {
          console.log(`Attempting backend logout actions for authMethod: ${authMethod}`);
          shouldContinueClientSideLogout = await performBackendLogout(authMethod, userNameForLogout);
          
          if (!shouldContinueClientSideLogout) {
              // This means performBackendLogout initiated SAML form submission (which navigates).
              // No further client-side action (localStorage.clear, redirect) is needed here.
              console.log(`Backend ${authMethod} logout initiated IdP form submission. Halting further client-side script execution in this function.`);
              return; // Stop execution of CheckAuthnMethod
          }
          // If shouldContinueClientSideLogout is true, it means:
          // 1. It was a "NORMAL" logout.
          // 2. SAML form submission wasn't applicable (e.g., no form in response).
          // 3. SAML form submission was attempted but failed internally (e.g., parsing error).
          // 4. The call to your backend's /v1/Logout endpoint itself failed.
          // In these cases, we proceed with client-side cleanup.
          console.log(`Backend logout call for ${authMethod} completed, or IdP form submission failed/skipped. Proceeding with client-side cleanup.`);
        } catch (logoutError) { // This catch is for unexpected errors from performBackendLogout itself
          const errorMessage = logoutError.response ? JSON.stringify(logoutError.response.data) : logoutError.message;
          console.warn(`Error during performBackendLogout for ${authMethod}: ${errorMessage}. Proceeding with client-side logout steps.`);
          shouldContinueClientSideLogout = true; // Ensure cleanup happens
        }
      } else {
        console.log(`No specific backend logout actions for authMethod: ${authMethod || "undefined"}. Proceeding with client-side cleanup.`);
      }

      // This code block will only run if shouldContinueClientSideLogout is true.
      if (shouldContinueClientSideLogout) {
        console.log("Proceeding with client-side storage clear and final redirect.");
        console.log("Before localStorage.clear():", JSON.stringify(localStorage));
        localStorage.clear();
        console.log("After localStorage.clear():", JSON.stringify(localStorage));

        const redirectURL = getRedirectUrl(authMethod, ssoUrl, currentDomain, REACT_APP_URL);
        console.log("Final client-side redirecting to:", redirectURL);

        if (redirectURL) {
          window.location.replace(redirectURL);
        } else {
          console.error("Could not determine final redirect URL. Fallback to generic login on current domain.");
          window.location.replace(buildLoginUrl(currentDomain, "/")); // Fallback
        }
      }
    })
    .catch((err) => { // Catches errors from axios.post('/getAuthMethod')
      const errorMessage = err.response ? JSON.stringify(err.response.data) : err.message;
      console.error("FATAL: Error fetching authentication method or during main logout process:", errorMessage);
      localStorage.clear();
      const fallbackDomain = currentDomain || "";
      window.location.replace(buildLoginUrl(fallbackDomain, "/tenant/login"));
    });
};

// Helper to determine the final redirect URL (this logic might need review depending on desired flow post-SLO)
const getRedirectUrl = (authMethod, ssoUrl, currentDomainFromStorage, appBaseReactUrl) => {
  let redirectURL;
  const isLocalDevEnvironment = appBaseReactUrl === "http://localhost:5173" || appBaseReactUrl === "http://localhost:5173/";

  if ((authMethod === "ONELOGIN" || authMethod === "OKTA") && ssoUrl) {
    // After IdP SLO, the IdP *should* redirect back to a configured URL.
    // This ssoUrl is likely the *login* URL for the IdP.
    // If the SAML form submission handles everything, this redirect might be a fallback
    // or for cases where the SAML form submission itself doesn't occur.
    redirectURL = ssoUrl; 
  } else if (authMethod === "NORMAL") {
    redirectURL = isLocalDevEnvironment
      ? buildLoginUrl("localhost:5173", "/org/login")
      : buildLoginUrl(currentDomainFromStorage, "/tenant/login");
  } else {
    // Default fallback if authMethod is unknown or not SAML/NORMAL
    redirectURL = buildLoginUrl(currentDomainFromStorage, "/tenant/login");
  }
  return redirectURL;
};

export const logOutUser = () => {
  CheckAuthnMethod();
};